import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';
import { useSessionStorage } from 'react-use';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { DiagnosticReportRecord } from '@models/diagnostic-report';
import type { GetFhirRecordParams } from '@models/fhir-resource';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useListLabResultDiagnosticReportsQuery,
  useListImagingDiagnosticReportsQuery,
  usePostLabReportDiagnosticReportRecordMutation,
  usePostImagingTestDiagnosticReportRecordMutation,
  useGetImagingDiagnosticReportQuery,
  useGetLabDiagnosticReportQuery,
  useUpdateLabDiagnosticReportMutation,
  useUpdateImagingDiagnosticReportMutation,
  useDeleteLabDiagnosticReportMutation,
  useDeleteImagingDiagnosticReportMutation,
} from 'generated/diagnostic-report.generated';
import type {
  PostLabReportDiagnosticReportRecordMutationVariables,
  PostImagingTestDiagnosticReportRecordMutationVariables,
  UpdateLabDiagnosticReportMutationVariables,
  UpdateImagingDiagnosticReportMutationVariables,
  DeleteLabDiagnosticReportMutation,
  DeleteLabDiagnosticReportMutationVariables,
  DeleteImagingDiagnosticReportMutation,
  DeleteImagingDiagnosticReportMutationVariables,
} from 'generated/diagnostic-report.generated';
import type { GetPatientHealthSummaryRecordsQuery } from 'generated/patient-record.generated';
import { GetPatientHealthSummaryRecordsDocument } from 'generated/patient-record.generated';

export const useGetLabDiagnosticReports = (params?: {
  page?: number;
  count?: number;
  patient?: string;
}) => {
  const { handleError } = useResponseHandler();
  const [token] = useSessionStorage('share_token');
  const {
    state: { activeProfile, isAuthenticated },
  } = useSession();
  const patient_id = params?.patient ?? activeProfile?.patient_id ?? '';
  const response = useListLabResultDiagnosticReportsQuery({
    variables: {
      input: {
        patient: patient_id,
        count: params?.count ?? 1000,
        page: params?.page ?? 1,
      },
    },
    skip: !patient_id,
    // Only add authorization header if resource is being shared (not authenticated)
    ...(!isAuthenticated && {
      context: {
        headers: {
          authorization: token,
        },
      },
    }),
  });
  const { data: paginatedData, error } = response;
  handleError(error);

  const records = (
    paginatedData?.listLabResultDiagnosticReports.data ?? []
  ).map((r) => new DiagnosticReportRecord(r));
  return {
    ...response,
    data: {
      ...paginatedData?.listLabResultDiagnosticReports,
      data: records,
    },
  };
};

export const useGetImagingDiagnosticReports = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListImagingDiagnosticReportsQuery({
    variables: {
      input: {
        patient: patient_id,
        count: 1500,
        page: 1,
      },
    },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);

  const records = (data?.listImagingDiagnosticReports.data ?? []).map(
    (r) => new DiagnosticReportRecord(r)
  );
  return { ...response, data: records };
};

export const useGetLabDiagnosticReport = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetLabDiagnosticReportQuery({
    variables: {
      input: {
        ...params,
        patient_id,
      },
    },
    skip:
      !patient_id ||
      !params.record_id ||
      params.record_id === 'add' ||
      Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getLabReport;
  return {
    ...response,
    data: record ? new DiagnosticReportRecord(record) : null,
  };
};

export const useGetImagingDiagnosticReport = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetImagingDiagnosticReportQuery({
    variables: {
      input: {
        ...params,
        patient_id,
      },
    },
    skip:
      !patient_id ||
      !params.record_id ||
      params.record_id === 'add' ||
      Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getImagingTest;
  return {
    ...response,
    data: record ? new DiagnosticReportRecord(record) : null,
  };
};

export function useCreateLabDiagnosticReport(): [
  (
    params: PostLabReportDiagnosticReportRecordMutationVariables
  ) => Promise<FetchResult<DiagnosticReportRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostLabReportDiagnosticReportRecordMutation();
  const loading = baseLoading;

  async function createLabDiagnosticReport(
    params: PostLabReportDiagnosticReportRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postLabReport) {
          return;
        }
        const storeObject = response.postLabReport as unknown as StoreObject;
        const { diagnosticReport } = params.input;
        try {
          cache.modify<{ listLabResultDiagnosticReports: CachedRecordList }>({
            fields: {
              listLabResultDiagnosticReports: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === diagnosticReport.subject?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });

          cache.updateQuery<GetPatientHealthSummaryRecordsQuery>(
            {
              query: GetPatientHealthSummaryRecordsDocument,
              variables: {
                input: {
                  patient: diagnosticReport.subject?.reference,
                },
                diagnostic_report: true,
              },
            },
            (data) => ({
              getPatientHealthSummaryRecords: {
                diagnostic_report: [
                  ...(data?.getPatientHealthSummaryRecords.diagnostic_report ??
                    []),
                  response.postLabReport,
                ],
              },
            })
          );
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdLab = response.data?.postLabReport;
    return {
      ...response,
      data: createdLab ? new DiagnosticReportRecord(createdLab) : undefined,
    };
  }
  return [createLabDiagnosticReport, loading];
}

export function useCreateImagingDiagnosticReport(): [
  (
    params: PostImagingTestDiagnosticReportRecordMutationVariables
  ) => Promise<FetchResult<DiagnosticReportRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostImagingTestDiagnosticReportRecordMutation();
  const loading = baseLoading;

  async function createImagingDiagnosticReport(
    params: PostImagingTestDiagnosticReportRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postImagingTest) {
          return;
        }
        const storeObject = response.postImagingTest as unknown as StoreObject;
        const { diagnosticReport } = params.input;
        try {
          cache.modify<{ listImagingDiagnosticReports: CachedRecordList }>({
            fields: {
              listImagingDiagnosticReports: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === diagnosticReport.subject?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });

          cache.updateQuery<GetPatientHealthSummaryRecordsQuery>(
            {
              query: GetPatientHealthSummaryRecordsDocument,
              variables: {
                input: {
                  patient: diagnosticReport.subject?.reference,
                },
                diagnostic_report: true,
              },
            },
            (data) => ({
              getPatientHealthSummaryRecords: {
                diagnostic_report: [
                  ...(data?.getPatientHealthSummaryRecords.diagnostic_report ??
                    []),
                  response.postImagingTest,
                ],
              },
            })
          );
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdImg = response.data?.postImagingTest;
    return {
      ...response,
      data: createdImg ? new DiagnosticReportRecord(createdImg) : undefined,
    };
  }
  return [createImagingDiagnosticReport, loading];
}

export function useUpdateLabDiagnosticReport(): [
  (
    params: UpdateLabDiagnosticReportMutationVariables
  ) => Promise<FetchResult<DiagnosticReportRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateLabDiagnosticReportMutation();
  const loading = baseLoading;

  async function updateLabDiagnosticReport(
    params: UpdateLabDiagnosticReportMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      // TODO: May need to add newly made lab observations to cache
    });
    const updatedLab = response.data?.updateLabReport;
    return {
      ...response,
      data: updatedLab ? new DiagnosticReportRecord(updatedLab) : undefined,
    };
  }
  return [updateLabDiagnosticReport, loading];
}

export function useUpdateImagingDiagnosticReport(): [
  (
    params: UpdateImagingDiagnosticReportMutationVariables
  ) => Promise<FetchResult<DiagnosticReportRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateImagingDiagnosticReportMutation();
  const loading = baseLoading;

  async function updateImagingDiagnosticReport(
    params: UpdateImagingDiagnosticReportMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      // TODO: May need to add newly made lab observations to cache
    });
    const updatedImg = response.data?.updateImagingTest;
    return {
      ...response,
      data: updatedImg ? new DiagnosticReportRecord(updatedImg) : undefined,
    };
  }
  return [updateImagingDiagnosticReport, loading];
}

export function useDeleteLabDiagnosticReport(): [
  (
    params: DeleteLabDiagnosticReportMutationVariables
  ) => Promise<FetchResult<DeleteLabDiagnosticReportMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteLabDiagnosticReportMutation();
  const loading = baseLoading;

  async function deleteLabDiagnosticReport(
    params: DeleteLabDiagnosticReportMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteLabReport) {
          return;
        }
        const { diagnostic_report_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'DiagnosticReportModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteLabDiagnosticReport, loading];
}

export function useDeleteImagingDiagnosticReport(): [
  (
    params: DeleteImagingDiagnosticReportMutationVariables
  ) => Promise<FetchResult<DeleteImagingDiagnosticReportMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteImagingDiagnosticReportMutation();
  const loading = baseLoading;

  async function deleteImagingDiagnosticReport(
    params: DeleteImagingDiagnosticReportMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteImagingTest) {
          return;
        }
        const { diagnostic_report_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'DiagnosticReportModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteImagingDiagnosticReport, loading];
}
